import React from "react";
import PropTypes from "prop-types";
import {
  TvRow,
  TvCol,
  TvFormGroup,
  TvLabel,
  TvInput,
  TvButton,
  TvHr,
  TvDiv,
  TvP,
  TvStrong,
} from "@trustvid/design-system";
import { PORTABLE_IMPORT_MAX_FILE_BYTES } from "../../../../utils/portable-campaign-import-file";

const maxFileMb = PORTABLE_IMPORT_MAX_FILE_BYTES / (1024 * 1024);

/**
 * SuperAdmin portable campaign JSON import: upload a file and/or paste, validate, create draft.
 */
function ImportCampaignJsonPanel({
    importJson,
    importFileLabel,
    fileInputRef,
    importValidation,
    isImporting,
    onImportJsonChange,
    onImportFileSelected,
    onValidateImport,
    onImportCreateDraft
}) {
    return (
        <>
            <TvHr className="my-4" />
            <TvDiv className="border rounded p-3">
                <TvRow className="mb-3">
                    <TvCol md={12}>
                        <TvLabel className="fw-semibold d-block mb-2">Import Campaign JSON (SuperAdmin only)</TvLabel>
                        <TvP className="text-muted small mb-2">
                            Upload a <TvStrong>.json</TvStrong> file below, <TvStrong>or</TvStrong> paste JSON into the box. You can
                            edit the text after uploading.
                        </TvP>
                        <TvFormGroup className="mb-2">
                            <TvLabel for="portable-import-file-input" className="fw-semibold">
                                Upload JSON file
                            </TvLabel>
                            <TvInput
                                id="portable-import-file-input"
                                name="portable_import_json"
                                type="file"
                                accept=".json,application/json"
                                innerRef={fileInputRef}
                                onChange={onImportFileSelected}
                                className="mt-1"
                                data-testid="portable-import-file-input"
                                aria-describedby="import-json-security-note"
                            />
                            {importFileLabel ? (
                                <TvDiv className="small text-success mt-1" data-testid="import-file-loaded-label">
                                    Loaded: {importFileLabel}
                                </TvDiv>
                            ) : null}
                        </TvFormGroup>
                        <TvP className="text-muted small mb-0" id="import-json-security-note">
                            File uploads: only <TvStrong>.json</TvStrong>, max <TvStrong>{maxFileMb}</TvStrong> MB, UTF-8 text. Other
                            fields are not validated as strictly as file uploads.
                        </TvP>
                    </TvCol>
                </TvRow>
                <TvRow>
                    <TvCol md={12}>
                        <TvFormGroup>
                            <TvLabel for="import-json-textarea">Paste or edit portable settings JSON</TvLabel>
                            <TvInput
                                id="import-json-textarea"
                                type="textarea"
                                rows={10}
                                value={importJson}
                                onChange={(e) => onImportJsonChange(e.target.value)}
                                placeholder="Paste portable_settings JSON here, or upload a file above"
                                className="mt-1"
                                data-testid="import-json-textarea"
                                spellCheck="false"
                                autoComplete="off"
                            />
                        </TvFormGroup>
                    </TvCol>
                </TvRow>
                <TvDiv className="d-flex justify-content-end gap-2">
                    <TvButton color="info" disabled={isImporting} onClick={onValidateImport}>
                        {isImporting ? "Validating..." : "Validate Import"}
                    </TvButton>
                    <TvButton color="success" disabled={isImporting} onClick={onImportCreateDraft}>
                        {isImporting ? "Importing..." : "Import JSON & Create Draft"}
                    </TvButton>
                </TvDiv>
                {importValidation && (
                    <TvDiv className="mt-3 p-3 border rounded bg-light">
                        <TvStrong>Validation Summary</TvStrong>
                        <TvDiv>Valid: {String(importValidation?.valid ?? false)}</TvDiv>
                        <TvDiv>Task Count: {importValidation?.draft_preview?.task_count ?? 0}</TvDiv>
                        <TvDiv>Default Actor ID: {importValidation?.actor_report?.default_actor_id ?? "N/A"}</TvDiv>
                        <TvDiv>Asset Candidates: {importValidation?.asset_report?.total_candidates ?? 0}</TvDiv>
                        <TvDiv>Assets Rehydrated: {importValidation?.asset_report?.rehydrated ?? 0}</TvDiv>
                        <TvDiv>Duplicate Skipped: {importValidation?.asset_report?.duplicate_skipped ?? 0}</TvDiv>
                    </TvDiv>
                )}
            </TvDiv>
        </>
    );
}

ImportCampaignJsonPanel.propTypes = {
    importJson: PropTypes.string.isRequired,
    importFileLabel: PropTypes.string,
    fileInputRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
    importValidation: PropTypes.shape({
        valid: PropTypes.bool,
        draft_preview: PropTypes.shape({
            task_count: PropTypes.number
        }),
        actor_report: PropTypes.shape({
            default_actor_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        }),
        asset_report: PropTypes.shape({
            total_candidates: PropTypes.number,
            rehydrated: PropTypes.number,
            duplicate_skipped: PropTypes.number
        })
    }),
    isImporting: PropTypes.bool.isRequired,
    onImportJsonChange: PropTypes.func.isRequired,
    onImportFileSelected: PropTypes.func.isRequired,
    onValidateImport: PropTypes.func.isRequired,
    onImportCreateDraft: PropTypes.func.isRequired
};

export default ImportCampaignJsonPanel;
